import React, { useEffect, useState } from 'react';
import AdminSidebar from './AdminSidebar';
import AdminHeader from './AdminHeader';
import axios from 'axios';

const base_url = process.env.REACT_APP_API_URL;

const formatDate = (date) => {
  return date.toLocaleDateString('en-GB').split('/').join('-');
};

const getLast7Dates = () => {
  const dates = [];
  for (let i = 7; i >= 1; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    dates.push(formatDate(date));
  }
  return dates;
};

const getDateRange = (start, end) => {
  const dates = [];
  let current = new Date(start);
  while (current <= new Date(end)) {
    dates.push(formatDate(new Date(current)));
    current.setDate(current.getDate() + 1);
  }
  return dates;
};

const filterHistoryByDates = (data, selectedDates) => {
  const filtered = {};
  for (const sport in data) {
    const sportData = data[sport];
    const filteredDates = {};

    selectedDates.forEach(date => {
      if (sportData[date]) {
        filteredDates[date] = sportData[date];
      }
    });

    if (Object.keys(filteredDates).length > 0) {
      filtered[sport] = filteredDates;
    }
  }
  return filtered;
};

const WeekReport = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(!(window.innerWidth < 768));
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [originalHistory, setOriginalHistory] = useState({});
  const [history, setHistory] = useState({});
  const [dates, setDates] = useState(getLast7Dates());
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d;
  });
  const [endDate, setEndDate] = useState(() => new Date());

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const fetchHistory = async () => {
    try {
      const auth = localStorage.getItem('admin');
      const response = await axios.get(`${base_url}/api/webapi/weeklyReport`, {
        headers: {
          Accept: 'application/json',
          Authorization: `${auth}`,
        },
      });

      if (response?.data.success) {
        const raw = response.data.data;
        const formatted = {};
        raw.forEach(item => {
          const sport = item.sportname;
          const date = formatDate(new Date(item.report_date));
          const profit = Number(item.total_profit);
          const loss = Number(item.total_loss);

          if (!formatted[sport]) formatted[sport] = {};
          formatted[sport][date] = { profit, loss };
        });

        setOriginalHistory(formatted);

        const last7Dates = getLast7Dates();
        setDates(last7Dates);
        setHistory(filterHistoryByDates(formatted, last7Dates));
      }
    } catch (error) {
      console.error('Error fetching weekly report:', error);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchHistory();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setIsSidebarOpen(true);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const newDates = getDateRange(startDate, endDate);
    setDates(newDates);
    setHistory(filterHistoryByDates(originalHistory, newDates));
  }, [startDate, endDate, originalHistory]);

  const dateTotals = {};
  let overallProfit = 0;
  let overallLoss = 0;

  dates.forEach(date => {
    dateTotals[date] = { profit: 0, loss: 0 };
  });

  Object.values(history).forEach(sportData => {
    dates.forEach(date => {
      const data = sportData[date];
      const profit = data?.profit || 0;
      const loss = data?.loss || 0;
      dateTotals[date].profit += profit;
      dateTotals[date].loss += loss;
      overallProfit += profit;
      overallLoss += loss;
    });
  });

  return (
    <div className="wrapper">
      <AdminSidebar isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} isMobile={isMobile} />
      <AdminHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} isMobile={isMobile} />

      <div
        className={`content-wrapper d-flex flex-column ${!isMobile && isSidebarOpen ? "" : "open-content-wrapper"}`}
        style={{ marginLeft: (!isMobile && isSidebarOpen) ? "" : "0", transition: "margin-left 0.3s ease" }}
      >
        <section className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1>WEEKLY REPORT ({dates[0]} - {dates[dates.length - 1]})</h1>
              </div>
            </div>
          </div>
        </section>

        <div className="container-fluid">
          <div className="card">
            <div className="row">
              <div className="col-md-12">

                <div className="d-flex gap-2 m-3 align-items-center">
                  <label className="me-2">Filter Dates:</label>
                  <input
                    type="date"
                    className="form-control"
                    value={startDate.toISOString().slice(0, 10)}
                    onChange={(e) => setStartDate(new Date(e.target.value))}
                  />
                  <input
                    type="date"
                    className="form-control"
                    value={endDate.toISOString().slice(0, 10)}
                    onChange={(e) => setEndDate(new Date(e.target.value))}
                  />
                </div>

                <div className="mt-4 table-responsive">
                  <table className="table table-bordered table-striped">
                    <thead>
                      <tr>
                        <th className="text-center text-nowrap">Sport</th>
                        {dates.map((date, index) => (
                          <th key={index} className="text-center text-nowrap">{date}</th>
                        ))}
                        <th className="text-center text-nowrap">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.keys(history).map((sport, idx) => {
                        const sportData = history[sport];
                        let totalProfit = 0;
                        let totalLoss = 0;

                        return (
                          <tr key={idx}>
                            <td className="text-center fw-bold">{sport}</td>
                            {dates.map((date, i) => {
                              const data = sportData[date];
                              const profit = data?.profit || 0;
                              const loss = data?.loss || 0;
                              totalProfit += profit;
                              totalLoss += loss;
                              const net = profit - loss;

                              return (
                                <td key={i} className="text-center text-nowrap fw-bold">
                                  {net > 0 ? (
                                    <div className="text-danger">-{net}</div>
                                  ) : net < 0 ? (
                                    <div className="text-success">+{Math.abs(net)}</div>
                                  ) : (
                                    <div className="text-secondary">0</div>
                                  )}
                                </td>
                              );
                            })}
                            <td className="text-center text-nowrap fw-bold">
                              {totalProfit - totalLoss > 0 ? (
                                <div className="text-danger">-{totalProfit - totalLoss}</div>
                              ) : totalProfit - totalLoss < 0 ? (
                                <div className="text-success">+{Math.abs(totalProfit - totalLoss)}</div>
                              ) : (
                                <div className="text-secondary">0</div>
                              )}
                            </td>
                          </tr>
                        );
                      })}

                      {/* Total Row */}
                      <tr className="bg-light fw-bold">
                        <td className="text-center">Total</td>
                        {dates.map((date, idx) => {
                          const { profit, loss } = dateTotals[date];
                          const net = profit - loss;

                          return (
                            <td key={idx} className="text-center text-nowrap">
                              {net > 0 ? (
                                <div className="text-danger">-{net}</div>
                              ) : net < 0 ? (
                                <div className="text-success">+{Math.abs(net)}</div>
                              ) : (
                                <div className="text-secondary">0</div>
                              )}
                            </td>
                          );
                        })}
                        <td className="text-center text-nowrap">
                          {overallProfit - overallLoss > 0 ? (
                            <div className="text-danger">-{overallProfit - overallLoss}</div>
                          ) : overallProfit - overallLoss < 0 ? (
                            <div className="text-success">+{Math.abs(overallProfit - overallLoss)}</div>
                          ) : (
                            <div className="text-secondary">0</div>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

              </div>
            </div>
          </div>
        </div>

        <div className="mb-4" />
      </div>
    </div>
  );
};

export default WeekReport;
